/**
 * Formatação uniforme de quantidades no sistema (pt-BR).
 * Ex.: 99999.5 → "99.999,50"
 */

/** Interpreta número digitado/exibido em pt-BR (vírgula decimal). */
export function parseNumero(valor) {
  if (valor == null || valor === '') return NaN;
  if (typeof valor === 'number') return valor;
  const s = String(valor).trim();
  if (!s) return NaN;
  // Já no padrão BR (milhar . / decimal ,)
  if (s.includes(',')) {
    return parseFloat(s.replace(/\./g, '').replace(',', '.'));
  }
  return parseFloat(s);
}

/**
 * Sanitiza digitação: só dígitos e uma vírgula decimal.
 * Converte ponto digitado para vírgula (padrão BR).
 */
export function sanitizarEntradaDecimalBr(valor) {
  let s = String(valor ?? '').replace(/[^\d.,]/g, '');
  s = s.replace(/\./g, ',');
  const i = s.indexOf(',');
  if (i === -1) return s;
  return s.slice(0, i + 1) + s.slice(i + 1).replace(/,/g, '');
}

/** Número → texto de input com vírgula (sem milhar). Vazio se inválido/≤0 e `allowEmpty`. */
export function numeroParaEntradaBr(valor) {
  if (valor == null || valor === '') return '';
  const n = typeof valor === 'number' ? valor : parseNumero(valor);
  if (!Number.isFinite(n)) return '';
  // Evita "10,00" forçado; mantém casas necessárias até 4
  const fixed = Math.round(n * 10000) / 10000;
  return String(fixed).replace('.', ',');
}

/** Formata quantidade com milhar "." e 2 casas decimais ",". */
export function formatQuantidade(valor, empty = 'N/A') {
  const n = parseNumero(valor);
  if (!Number.isFinite(n)) return empty;
  return n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

/** Formata quantidade + unidade (ex.: "1.234,50 Kg"). */
export function formatQuantidadeComUnidade(valor, unidade, empty = 'N/A') {
  const q = formatQuantidade(valor, empty);
  if (q === empty) return empty;
  const u = unidade != null && String(unidade).trim() !== '' ? ` ${String(unidade).trim()}` : '';
  return `${q}${u}`;
}

/** Normaliza rótulos de unidade para agregação (Kg, Ton, L...). */
export function normalizarUnidadeMedida(unidade) {
  const raw = String(unidade ?? '').trim();
  if (!raw) return 'N/I';
  const validas = ['Kg', 'g', 'Ton', 'L', 'mL', 'Unid.'];
  if (validas.includes(raw)) return raw;
  const key = raw.toLowerCase().replace(/\s+/g, '');
  const map = {
    kg: 'Kg',
    kilograma: 'Kg',
    kilogramas: 'Kg',
    g: 'g',
    grama: 'g',
    gramas: 'g',
    ton: 'Ton',
    t: 'Ton',
    tonelada: 'Ton',
    toneladas: 'Ton',
    l: 'L',
    litro: 'L',
    litros: 'L',
    ml: 'mL',
    mililitro: 'mL',
    mililitros: 'mL',
    'unid.': 'Unid.',
    unid: 'Unid.',
    unidade: 'Unid.',
    unidades: 'Unid.',
    un: 'Unid.'
  };
  return map[key] || raw;
}

/**
 * Soma quantidades agrupadas por unidade.
 * @returns {Array<{ unidade: string, quantidade: number }>} ordenado por quantidade desc
 */
export function agregarQuantidadesPorUnidade(items = [], opts = {}) {
  const {
    quantidadeKey = 'quantidade_recebida',
    unidadeKey = 'unidade_medida',
    filterFn = null
  } = opts;
  const mapa = {};

  items.forEach((item) => {
    if (filterFn && !filterFn(item)) return;
    const qtd = Number(item?.[quantidadeKey] ?? item?.quantidade ?? 0) || 0;
    if (!qtd) return;
    const unidade = normalizarUnidadeMedida(
      item?.[unidadeKey] || item?.unidade || 'N/I'
    );
    mapa[unidade] = (mapa[unidade] || 0) + qtd;
  });

  return Object.entries(mapa)
    .map(([unidade, quantidade]) => ({ unidade, quantidade }))
    .sort((a, b) => b.quantidade - a.quantidade);
}

/** Converte objeto { Kg: 10, L: 2 } ou array em lista ordenada. */
export function toListaPorUnidade(source) {
  if (Array.isArray(source)) {
    return source
      .map((row) => ({
        unidade: normalizarUnidadeMedida(row.unidade || row.unit || 'N/I'),
        quantidade: Number(row.quantidade ?? row.total ?? 0) || 0
      }))
      .filter((row) => row.quantidade > 0)
      .sort((a, b) => b.quantidade - a.quantidade);
  }
  if (source && typeof source === 'object') {
    return Object.entries(source)
      .map(([unidade, quantidade]) => ({
        unidade: normalizarUnidadeMedida(unidade),
        quantidade: Number(quantidade) || 0
      }))
      .filter((row) => row.quantidade > 0)
      .sort((a, b) => b.quantidade - a.quantidade);
  }
  return [];
}

/** Texto multilinha: "1.234,50 Kg · 12,00 Ton" */
export function formatTotaisPorUnidade(lista, empty = '—') {
  const rows = toListaPorUnidade(lista);
  if (!rows.length) return empty;
  return rows
    .map((row) => formatQuantidadeComUnidade(row.quantidade, row.unidade))
    .join(' · ');
}
